<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="landing-page-modal-goal"
            :is-open="isOpen"
            :title="$t('funnel.edit.goal.landing.page.title')"
            :summary-text="$t('funnel.edit.goal.landing.page.message')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section class="landing-page-select">
                    <DsMultiselect
                        v-model="selectedLandingPage"
                        data-qa="landing-page-site"
                        class="select-landing-page-site"
                        searchable
                        show-search-icon
                        :force-invalid="forceInvalid && !landingPageSelected"
                        required
                        label-prop="title"
                        value-prop="id"
                        :placeholder="$t('funnel.edit.goal.landing.page.select.placeholder')"
                        :loading="loadingLandingPages"
                        :readonly="loadingLandingPages"
                        :options="landingPages"
                        allow-add
                        :add-text="$t('funnel.edit.goal.landing.page.create')"
                        @add="createLandingPage"
                        @input="loadLandingPageFormDropdown"
                        @updateSearch="searchLandingPage"
                    />
                    <DsOutlineButton
                        data-qa="edit-landing-page-site"
                        class="edit-landing-page"
                        :disabled="!isLadingPageSelected"
                        trailing-icon="external-link"
                        as="a"
                        target="_blank"
                        :href="editLandingPageLink"
                    >
                        <span>
                            {{ $t('funnel.edit.goal.landing.page.edit') }}
                        </span>
                    </DsOutlineButton>
                </section>
                <section>
                    <DsMultiselect
                        v-model="selectedLandingPageForm"
                        data-qa="landing-page-form"
                        class="select-landing-page-form"
                        :force-invalid="forceInvalid && !landingPageFormSelected"
                        required
                        :loading="loadingLandingPages || loadingLandingPageForms"
                        :readonly="loadingLandingPages || loadingLandingPageForms || !isLadingPageSelected"
                        label-prop="title"
                        value-prop="id"
                        :placeholder="selectFormPlaceholder"
                        :options="landingPageForms"
                    />
                </section>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    loadLandingPage,
    loadLandingPages,
    loadLandingPageForms,
    searchLandingPages,
} from './unlayer.landing.page.api';

export default {
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    unlayerLandingPageId: '',
                    unlayerLandingPageTitle: '',
                    unlayerFormIds: [],
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            forceInvalid: false,
            landingPages: [],
            landingPageForms: [],
            loadingLandingPages: false,
            loadingLandingPageForms: false,
            selectedLandingPageId: 0,
            selectedLandingPageFormIds: 0,
            selectedLandingPage: {},
            selectedLandingPageForm: {},
            newlyCreatedLandingPage: {},
        };
    },

    computed: {
        landingPageLink() {
            return `${this.appInfo.spaBaseUrl}/marketing-sites/create?${this.appInfo.spaAppParameter}`;
        },

        editLandingPageLink() {
            if (!this.isLadingPageSelected) {
                return '';
            }

            return `${this.appInfo.spaBaseUrl}/marketing-sites/${this.selectedLandingPage.id}/edit?${this.appInfo.spaAppParameter}`;
        },

        isLadingPageSelected() {
            return !!this.selectedLandingPage && !!this.selectedLandingPage?.id;
        },

        selectFormPlaceholder() {
            if (this.isLadingPageSelected && !this.loadingLandingPageForms
                    && (!this.landingPageForms || this.landingPageForms?.length === 0)) {
                return this.$t('funnel.edit.goal.landing.page.no.form.select.placeholder');
            }

            return this.$t('funnel.edit.goal.landing.page.form.select.placeholder');
        },

        isReady() {
            return this.landingPageSelected && this.landingPageFormSelected;
        },

        landingPageSelected() {
            return !!this.selectedLandingPage?.id;
        },

        landingPageFormSelected() {
            return !!this.selectedLandingPageForm?.id;
        },
    },

    async mounted() {
        this.selectedLandingPageId = this.cell?.value?.unlayerLandingPageId;
        this.selectedLandingPageFormIds = this.cell?.value?.unlayerFormIds;
        await this.loadLandingPageDropdown();
        await this.loadLandingPageFormDropdown();
        await this.loadConfig();
        document.addEventListener('visibilitychange', this.handleFocus);
    },

    unmounted() {
        document.removeEventListener('visibilitychange', this.handleFocus);
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },

        updateConfig() {
            this.forceInvalid = false;

            if (this.isReady) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.unlayerLandingPageId = this.selectedLandingPage.id;
                cell.value.unlayerLandingPageTitle = this.selectedLandingPage.title;
                cell.value.unlayerFormIds = [this.selectedLandingPageForm.id];

                this.$emit('update-cell-value', { cell });
            } else {
                this.forceInvalid = true;
            }
        },

        async loadConfig() {
            this.loadingLandingPages = true;
            this.loadingLandingPageForms = true;

            if (this.selectedLandingPageId) {
                if (this.landingPages && this.landingPages.length > 0) {
                    await this.loadSelectedLandingPageIfMissing(this.selectedLandingPageId);
                }
            }

            if (this.selectedLandingPageFormIds?.length > 0 && this.landingPageForms && this.landingPageForms.length > 0) {
                this.selectedLandingPageForm = this.landingPageForms.find((e) => this.selectedLandingPageFormIds.includes(e.id));
            }
            this.loadingLandingPages = false;
            this.loadingLandingPageForms = false;
        },

        async loadSelectedLandingPageIfMissing(landingPageId) {
            this.selectedLandingPage = this.landingPages.find((e) => e.id === landingPageId);

            if (!this.selectedLandingPage) {
                const selectedElement = await loadLandingPage(landingPageId);

                if (selectedElement) {
                    this.landingPages.push(selectedElement);
                    this.selectedLandingPage = selectedElement;
                }
            }
        },

        async loadLandingPageDropdown() {
            try {
                this.loadingLandingPages = true;

                const response = await loadLandingPages();

                if (this.landingPages?.length > 0) {
                    const existingLandingPages = this.landingPages;
                    const newLandingPage = response.filter((landingPage) => {
                        return !existingLandingPages.some((existingLandingPage) => existingLandingPage.id === landingPage.id);
                    });

                    if (newLandingPage.length > 0) {
                        const landingPage = newLandingPage[0];

                        this.newlyCreatedLandingPage = {
                            id: landingPage.id,
                            title: landingPage.title,
                        };
                    }
                }

                this.landingPages = response.map(({ id, title }) => ({ id, title }));
                this.loadingLandingPages = false;
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.goal.landing.page.error.loading') });
            }
        },

        async searchLandingPage(inputValue) {
            if (!inputValue) {
                return;
            }

            try {
                this.loadingLandingPages = true;
                const response = await searchLandingPages(inputValue);

                this.landingPages = response.map(({ id, title }) => ({ id, title }));
                this.loadingLandingPages = false;
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.goal.landing.page.error.loading') });
            }
        },

        async handleFocus() {
            await this.$nextTick();

            if (!document.hidden) {
                await this.loadLandingPageDropdown();

                const selectedForm = this.selectedLandingPageForm;
                const landingPage = this.isLadingPageSelected
                    ? this.selectedLandingPage
                    : this.newlyCreatedLandingPage;

                await this.loadSelectedLandingPageIfMissing(landingPage.id);
                await this.loadLandingPageFormDropdown();

                const foundForm = this.landingPageForms.find((item) => item.id === selectedForm.id);

                this.selectedLandingPageForm = foundForm || {};
                this.newlyCreatedLandingPage = {};
            }
        },

        async loadLandingPageFormDropdown() {
            if (!this.isLadingPageSelected) {
                if (this.selectedLandingPageId) {
                    this.selectedLandingPage = { id: this.selectedLandingPageId };
                } else {
                    return;
                }
            }

            try {
                this.loadingLandingPageForms = true;
                this.selectedLandingPageForm = {};
                const response = await loadLandingPageForms(this.selectedLandingPage.id);

                this.landingPageForms = response.map(({ label, value }) => ({
                    id: value,
                    title: label,
                }));
                this.loadingLandingPageForms = false;
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.goal.landing.page.error.loading') });
            }
        },
        createLandingPage() {
            window.open(this.landingPageLink, '_blank');
        },
    },
};
</script>

<style lang="scss" scoped>
.edit-landing-page {
    width: px-to-rem(230);
    height: px-to-rem(40);
}
.select-landing-page-site {
    margin-right: $spacing-150;
    margin-bottom: $spacing-200;
}
.landing-page-select {
    display: flex;
}
.editors-alert {
    margin: $spacing-200 auto;
}
.create-landing-page-link {
    border: none;
}
.resume-editor {
    border: none;
    padding-left: 0;
}
</style>

<style lang="scss">
[data-qa="Create a new landing page"] {
    background-image: url("/resources/svg/external-link_blue.svg");
    background-repeat: no-repeat;
    background-position: px-to-rem(240) px-to-rem(18);
    background-size: px-to-rem(16) px-to-rem(16);
}
</style>
